import type { Timestamp } from 'firebase/firestore';

// Re-export settings types
export * from './settings';

// ============================================================================
// TYPE DISCRIMINATORS
// ============================================================================

export type ItemType = 'task' | 'transaction' | 'note' | 'keyDate';

// ============================================================================
// STATUS TYPES
// IMPORTANT: Keep in sync with FilterBar.tsx getStatusOptions()
// ============================================================================
export type TaskStatus = 'new' | 'inProgress' | 'complete';
export type TransactionStatus = 'draft' | 'pending' | 'final';
export type NoteKeyDateStatus = 'draft' | 'final';

// Union of all valid occurrence statuses
// IMPORTANT: Runtime validation via helper functions enforces type-appropriate values:
// - Tasks: 'new' | 'inProgress' | 'complete'
// - Transactions: 'draft' | 'pending' | 'final'
// - Notes/KeyDates: 'draft' | 'final'
export type OccurrenceStatus = TaskStatus | TransactionStatus | NoteKeyDateStatus;

export type Priority = 'critical' | 'high' | 'medium' | 'low' | 'future';
export type RecurrenceType = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';

// ============================================================================
// SUPPORTING TYPES
// ============================================================================

export type NoteColor =
  | 'default'
  | 'gray'
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'lime'
  | 'green'
  | 'teal'
  | 'cyan'
  | 'blue'
  | 'indigo'
  | 'purple'
  | 'pink';

export interface Reminder {
  id: string;
  daysBefore: number; // Days before dueDate to remind (0 = same day)
  time?: string;      // Time of day (e.g., "09:00")
  sent?: boolean;
}

export interface Attachment {
  id: string;
  name: string;
  url: string;
  path: string;
  size: number;
  mimeType: string;
  uploadedAt: Timestamp;
  order?: number;  // Global order shared with sub-items for unified list
}

// ============================================================================
// OCCURRENCE TYPE (For per-occurrence tracking in recurring items)
// ============================================================================

export interface Occurrence {
  date: Date;
  status: OccurrenceStatus;  // Type-specific status validated at runtime
  completedAt?: Date;   // Set when status becomes 'complete' (tasks only)
  finalizedAt?: Date;   // Set when status becomes 'final' (transactions/notes/keyDates)
}

// ============================================================================
// BASE FIELD INTERFACES (Building Blocks)
// ============================================================================

/**
 * Fields common to ALL items and sub-items
 */
interface ItemBaseFields {
  id: string;
  type: ItemType;
  name: string;
  description?: string;
  category?: string;
  subCategory?: string;
  owner?: string;
  reminders?: Reminder[];
  attachments?: Attachment[];
  createdBy: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
  priority?: Priority;
  effortHours?: number;  // Effort time in hours (0.25 = 15 min, max 168 = 1 week)
  // Stores previous status before marking complete/final, used to restore on toggle
  lastKnownStatus?: string;
  source?: string;  // Origin of item creation (e.g., 'chrome-extension')
}

/**
 * Fields common to ALL items but NOT sub-items
 */
interface ItemOnlyBaseFields {
  itemGroup?: string;  // Scoped to category+subCategory; Items only, not sub-items
}

/**
 * Fields for date-capable items (task, transaction, keyDate - NOT notes)
 */
interface DateCapableFields {
  dueDate?: Timestamp;              // Used when recurrence = 'none'
  startDate?: Timestamp;            // Used when recurrence != 'none'
  endDate?: Timestamp;              // Used when recurrence != 'none'
  endDateForever?: boolean;         // True if "Forever" checked
  recurrence?: RecurrenceType;
  daysOfWeek?: number[];            // 0-6 (Sun-Sat) for weekly recurrence
  daysOfMonth?: (number | string)[]; // 1-31, 'last', 'First Sat', 'Second Sun', etc.
  datesOfYear?: string[];           // "MM-DD" format for yearly recurrence (e.g., "01-15", "03-20")
  occurrences?: Occurrence[];       // Per-occurrence tracking for recurring items (180-day horizon)
  completedAt?: Timestamp;
  finalizedAt?: Timestamp;
}

/**
 * Simplified date fields for sub-items (no recurrence - only one-time dueDate)
 */
interface SubItemDateFields {
  dueDate?: Timestamp;
  completedAt?: Timestamp;
  finalizedAt?: Timestamp;
}

/**
 * Task-specific fields for Items (includes choreWheel)
 */
interface TaskItemFields {
  status: TaskStatus;
  choreWheel?: boolean;
}

/**
 * Task-specific fields for SubItems (no choreWheel)
 */
interface TaskSubItemFields {
  status: TaskStatus;
}

/**
 * Transaction-specific fields
 */
interface TransactionFields {
  transactionType: 'expense' | 'income';
  amount: number;              // In cents
  currency: string;
  entityId?: string;           // Link to Entity (bank/institution/counterparty)
  accountId?: string;          // Link to Account (specific account at entity)
  status: TransactionStatus;
}

/**
 * Note-specific fields
 */
export interface AiSummary {
  label: string;            // Always "AI Summary"
  summaryTitle: string;     // Page/article title
  summaryDateTimeUTC: string; // ISO 8601 UTC timestamp of when summary was generated
  summaryContent: string;   // The actual AI summary text
}

interface NoteFields {
  content?: string;
  fullWebPageHTML?: string;
  aiSummary?: AiSummary;
  color?: NoteColor;
  tags?: string[];
  status?: NoteKeyDateStatus;
}

/**
 * KeyDate-specific fields
 */
interface KeyDateFields {
  status?: NoteKeyDateStatus;
}

/**
 * URL-capable fields (task, transaction, note - NOT keyDate)
 */
interface UrlCapableFields {
  urls?: string[];
}

/**
 * Fields only on top-level Items (not sub-items)
 */
interface ItemOnlyFields {
  userId: string;
  subItemCount?: number;
  completedSubItemCount?: number;
}

/**
 * Fields only on SubItems (not top-level items)
 */
interface SubItemOnlyFields {
  parentId: string;
  order: number;
}

// ============================================================================
// COMPOSED ITEM TYPES (Discriminated Unions)
// ============================================================================

/**
 * Task Item - has dueDate, status, urls, choreWheel
 */
export type TaskItem = ItemBaseFields
  & ItemOnlyBaseFields
  & DateCapableFields
  & TaskItemFields
  & UrlCapableFields
  & ItemOnlyFields
  & { type: 'task' };

/**
 * Transaction Item - has amount, dueDate, status, urls
 */
export type TransactionItem = ItemBaseFields
  & ItemOnlyBaseFields
  & DateCapableFields
  & TransactionFields
  & UrlCapableFields
  & ItemOnlyFields
  & { type: 'transaction' };

/**
 * Note Item - has content, color, tags, urls, and optional dueDate for reminders
 */
export type NoteItem = ItemBaseFields
  & ItemOnlyBaseFields
  & DateCapableFields
  & NoteFields
  & UrlCapableFields
  & ItemOnlyFields
  & { type: 'note' };

/**
 * KeyDate Item - has dueDate, recurrence (NO urls)
 */
export type KeyDateItem = ItemBaseFields
  & ItemOnlyBaseFields
  & DateCapableFields
  & KeyDateFields
  & ItemOnlyFields
  & { type: 'keyDate' };

/**
 * Union of all Item types
 */
export type Item = TaskItem | TransactionItem | NoteItem | KeyDateItem;

// ============================================================================
// COMPOSED SUBITEM TYPES (Mirror Item structure with parentId instead of userId)
// ============================================================================

/**
 * Task SubItem - simplified fields (no choreWheel, no recurrence)
 */
export type TaskSubItem = ItemBaseFields
  & SubItemDateFields
  & TaskSubItemFields
  & UrlCapableFields
  & SubItemOnlyFields
  & { type: 'task' };

/**
 * Transaction SubItem - simplified fields (no recurrence)
 */
export type TransactionSubItem = ItemBaseFields
  & SubItemDateFields
  & TransactionFields
  & UrlCapableFields
  & SubItemOnlyFields
  & { type: 'transaction' };

/**
 * Note SubItem - simplified fields (no recurrence)
 */
export type NoteSubItem = ItemBaseFields
  & SubItemDateFields
  & NoteFields
  & UrlCapableFields
  & SubItemOnlyFields
  & { type: 'note' };

/**
 * KeyDate SubItem - simplified fields (no recurrence)
 */
export type KeyDateSubItem = ItemBaseFields
  & SubItemDateFields
  & KeyDateFields
  & SubItemOnlyFields
  & { type: 'keyDate' };

/**
 * Union of all SubItem types
 */
export type SubItem = TaskSubItem | TransactionSubItem | NoteSubItem | KeyDateSubItem;

// ============================================================================
// INPUT TYPES (For creating/updating items)
// ============================================================================

/**
 * Input for creating a new Item
 */
export interface CreateItemInput {
  type: ItemType;
  name: string;
  description?: string;
  category?: string;
  subCategory?: string;
  itemGroup?: string; // Scoped to category+subCategory; Items only
  priority?: Priority;
  effortHours?: number; // Effort time in hours (0.25 = 15 min, max 168 = 1 week)
  organizationId?: string; // Organization context for sharing
  owner?: string; // User ID of item owner (can be different from creator)

  // Status fields (Task: new/inProgress/complete, Transaction/Note/KeyDate: pending/completed)
  status?: TaskStatus | TransactionStatus | NoteKeyDateStatus;

  // Task-specific fields
  choreWheel?: boolean;

  // Transaction fields
  transactionType?: 'expense' | 'income';
  amount?: number;
  currency?: string;
  entityId?: string;
  accountId?: string;

  // Note fields
  content?: string;
  fullWebPageHTML?: string;
  aiSummary?: AiSummary;
  color?: NoteColor;
  tags?: string[];

  // Date-capable fields
  dueDate?: Date;
  startDate?: Date;
  endDate?: Date;
  endDateForever?: boolean;
  recurrence?: RecurrenceType;
  daysOfWeek?: number[];
  daysOfMonth?: (number | string)[];
  datesOfYear?: string[];

  // URLs
  urls?: string[];

  // Reminders
  reminders?: Reminder[];
}

/**
 * Input for updating an existing Item
 */
export interface UpdateItemInput extends Partial<Omit<CreateItemInput, 'type'>> {
  // Task-specific updates
  completedAt?: Date | null;
  // Non-task finalization
  finalizedAt?: Date | null;

  // Status restoration - stores previous status before marking complete/final
  lastKnownStatus?: string;

  // Occurrence-level tracking for recurring items
  occurrences?: Occurrence[];
}

/**
 * Input for creating a SubItem (parentId and order added by service)
 */
export interface CreateSubItemInput extends CreateItemInput {}

/**
 * Input for updating a SubItem
 */
export interface UpdateSubItemInput extends UpdateItemInput {}

// ============================================================================
// CONSTANTS (Field Classification)
// ============================================================================

/**
 * Canonical order for displaying item types throughout the app
 */
export const ITEM_TYPE_ORDER: ItemType[] = ['task', 'keyDate', 'transaction', 'note'];

/**
 * Types that have schedulable/date-capable fields (dueDate, recurrence, etc.)
 */
export const SCHEDULABLE_TYPES: ItemType[] = ['task', 'keyDate', 'transaction', 'note'];

/**
 * Types that have URLs field
 */
export const URL_TYPES: ItemType[] = ['task', 'transaction', 'note'];

/**
 * Fields common to ALL item types
 */
export const COMMON_FIELDS = [
  'name', 'description', 'category', 'subCategory',
  'owner', 'priority', 'effortHours', 'reminders', 'attachments'
] as const;

/**
 * Fields for schedulable types (task, keyDate, transaction)
 */
export const SCHEDULABLE_FIELDS = [
  'dueDate', 'recurrence', 'daysOfWeek', 'daysOfMonth', 'completedAt', 'finalizedAt'
] as const;

/**
 * Task-specific fields
 */
export const TASK_SPECIFIC_FIELDS = ['status', 'choreWheel'] as const;

/**
 * Transaction-specific fields
 */
export const TRANSACTION_SPECIFIC_FIELDS = [
  'transactionType', 'amount', 'currency', 'entityId', 'accountId', 'status'
] as const;

/**
 * Note-specific fields
 */
export const NOTE_SPECIFIC_FIELDS = ['content', 'color', 'tags', 'status'] as const;

/**
 * KeyDate-specific fields
 */
export const KEYDATE_SPECIFIC_FIELDS = ['status'] as const;

/**
 * URL field (for types that support it)
 */
export const URL_FIELDS = ['urls'] as const;

// ============================================================================
// HELPER FUNCTIONS (Field Classification)
// ============================================================================

/**
 * Check if a type has schedulable fields (dueDate, recurrence, etc.)
 */
export function isSchedulableType(type: ItemType): boolean {
  return SCHEDULABLE_TYPES.includes(type);
}

/**
 * Check if a type has URLs field
 */
export function hasUrls(type: ItemType): boolean {
  return URL_TYPES.includes(type);
}

/**
 * Get the default status for a given item type
 * IMPORTANT: Keep in sync with FilterBar.tsx getStatusOptions()
 */
export function getDefaultStatus(type: ItemType): TaskStatus | TransactionStatus | NoteKeyDateStatus | undefined {
  switch (type) {
    case 'task':
      return 'new';
    case 'transaction':
    case 'note':
    case 'keyDate':
      return 'draft';
    default:
      return undefined;
  }
}

/**
 * Get the "complete" status value for a given item type
 * Task uses 'complete', others use 'final'
 */
export function getCompleteStatus(type: ItemType): string {
  return type === 'task' ? 'complete' : 'final';
}

/**
 * Check if a status represents "complete/done" for any item type
 */
export function isCompleteStatus(status: string | undefined): boolean {
  return status === 'complete' || status === 'final';
}

// ============================================================================
// OCCURRENCE STATUS HELPER FUNCTIONS
// These enforce type-specific status values at runtime via switch/case logic
// ============================================================================

/**
 * Get valid occurrence statuses for an item type.
 * Enforces type-specific status values at runtime.
 */
export function getValidOccurrenceStatuses(type: ItemType): OccurrenceStatus[] {
  switch (type) {
    case 'task':
      return ['new', 'inProgress', 'complete'];
    case 'transaction':
      return ['draft', 'pending', 'final'];
    case 'note':
    case 'keyDate':
      return ['draft', 'final'];
  }
}

/**
 * Get default occurrence status for an item type.
 */
export function getDefaultOccurrenceStatus(type: ItemType): OccurrenceStatus {
  switch (type) {
    case 'task':
      return 'new';
    case 'transaction':
      return 'draft';
    case 'note':
    case 'keyDate':
      return 'draft';
  }
}

/**
 * Get "complete" occurrence status for an item type.
 * Tasks use 'complete', all others use 'final'.
 */
export function getCompleteOccurrenceStatus(type: ItemType): OccurrenceStatus {
  switch (type) {
    case 'task':
      return 'complete';
    case 'transaction':
    case 'note':
    case 'keyDate':
      return 'final';
  }
}

/**
 * Check if an occurrence status represents "complete/done" for any item type.
 */
export function isOccurrenceComplete(status: OccurrenceStatus): boolean {
  return status === 'complete' || status === 'final';
}

/**
 * Validate that a status is valid for the given item type.
 * Returns false if status is not allowed for that type.
 */
export function isValidOccurrenceStatus(type: ItemType, status: string): boolean {
  return getValidOccurrenceStatuses(type).includes(status as OccurrenceStatus);
}

/**
 * Get all applicable field names for a given item type
 */
export function getFieldsForType(type: ItemType): string[] {
  const fields: string[] = [...COMMON_FIELDS];

  if (isSchedulableType(type)) {
    fields.push(...SCHEDULABLE_FIELDS);
  }

  switch (type) {
    case 'task':
      fields.push(...TASK_SPECIFIC_FIELDS);
      break;
    case 'transaction':
      fields.push(...TRANSACTION_SPECIFIC_FIELDS);
      break;
    case 'note':
      fields.push(...NOTE_SPECIFIC_FIELDS);
      break;
    case 'keyDate':
      fields.push(...KEYDATE_SPECIFIC_FIELDS);
      break;
  }

  if (hasUrls(type)) {
    fields.push(...URL_FIELDS);
  }

  return fields;
}

/**
 * Get fields that should be retained when switching from one type to another
 */
export function getRetainedFields(fromType: ItemType, toType: ItemType): string[] {
  const retained: string[] = [...COMMON_FIELDS];

  // Retain schedulable fields if both types are schedulable
  if (isSchedulableType(fromType) && isSchedulableType(toType)) {
    retained.push(...SCHEDULABLE_FIELDS);
  }

  // Retain URLs if both types have URLs
  if (hasUrls(fromType) && hasUrls(toType)) {
    retained.push(...URL_FIELDS);
  }

  return retained;
}

/**
 * Get fields that should be stashed when switching from one type to another
 */
export function getStashedFields(fromType: ItemType, toType: ItemType): string[] {
  const stashed: string[] = [];
  const retainedSet = new Set(getRetainedFields(fromType, toType));

  // Get all fields for the 'from' type
  const fromFields = getFieldsForType(fromType);

  // Stash fields that exist in 'from' but are not retained
  for (const field of fromFields) {
    if (!retainedSet.has(field)) {
      stashed.push(field);
    }
  }

  return stashed;
}

/**
 * Get type-specific fields for a given item type
 */
export function getTypeSpecificFields(type: ItemType): readonly string[] {
  switch (type) {
    case 'task':
      return TASK_SPECIFIC_FIELDS;
    case 'transaction':
      return TRANSACTION_SPECIFIC_FIELDS;
    case 'note':
      return NOTE_SPECIFIC_FIELDS;
    case 'keyDate':
      return KEYDATE_SPECIFIC_FIELDS;
  }
}

/**
 * Get display label for an item type
 */
export function getItemTypeLabel(type: ItemType): string {
  switch (type) {
    case 'task':
      return 'Task';
    case 'keyDate':
      return 'Key Date';
    case 'transaction':
      return 'Transaction';
    case 'note':
      return 'Note';
  }
}

/**
 * Get icon name for an item type (Lucide icon names)
 */
export function getItemTypeIcon(type: ItemType): string {
  switch (type) {
    case 'task':
      return 'CheckSquare';
    case 'keyDate':
      return 'Calendar';
    case 'transaction':
      return 'DollarSign';
    case 'note':
      return 'FileText';
  }
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

export function isTaskItem(item: Item): item is TaskItem {
  return item.type === 'task';
}

export function isTransactionItem(item: Item): item is TransactionItem {
  return item.type === 'transaction';
}

export function isNoteItem(item: Item): item is NoteItem {
  return item.type === 'note';
}

export function isKeyDateItem(item: Item): item is KeyDateItem {
  return item.type === 'keyDate';
}

export function isTaskSubItem(subItem: SubItem): subItem is TaskSubItem {
  return subItem.type === 'task';
}

export function isTransactionSubItem(subItem: SubItem): subItem is TransactionSubItem {
  return subItem.type === 'transaction';
}

export function isNoteSubItem(subItem: SubItem): subItem is NoteSubItem {
  return subItem.type === 'note';
}

export function isKeyDateSubItem(subItem: SubItem): subItem is KeyDateSubItem {
  return subItem.type === 'keyDate';
}

// ============================================================================
// USER TYPE
// ============================================================================

export interface User {
  id: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  defaultCurrency: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  // Organization & Role
  organizationId?: string;
  role?: import('./settings').UserRole;
  preferences?: import('./settings').UserPreferences;
  // MFA fields
  mfaEnabled?: boolean;
  mfaEnrolledAt?: Timestamp;
  mfaBackupCodesRemaining?: number;
  mfaSkip?: boolean; // SuperAdmin toggle: skip MFA on login (enrollment stays intact)
}

// ============================================================================
// NOTIFICATION TYPE
// ============================================================================

export type NotificationStatus = 'pending' | 'delivered' | 'clear';

export interface Notification {
  id: string;
  itemId: string;
  subItemId?: string;
  itemType: ItemType;
  itemName: string;
  message: string;
  dueDate: Timestamp;
  createdAt: Timestamp;
  status: NotificationStatus;
  notificationDateTimeUTC: Timestamp;
  reminderTime: string;
  reminderDaysBefore: number;
  userTimezone: string;
  // RBAC fields
  organizationId: string;
  createdBy: string;
  recipientUserId: string;
  occurrenceDate?: Timestamp;
  recurrence?: RecurrenceType;
  // Enriched item context for filtering
  category?: string;
  subCategory?: string;
  owner?: string;
  priority?: Priority;
  itemStatus?: string;
  entityId?: string;
  accountId?: string;
  choreWheel?: boolean;
}
